import { Component, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@app/app.service';
import { TreeSelectDialogComponent } from '@app/dialogs-helper/tree-select-dialog/tree-select-dialog.component';
import { stripHtml } from '@fui/fui-helpers';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { FieldConfig } from '../field.interface';

@Component({
    selector: 'app-input-tree-select',
    templateUrl: './input-tree-select.component.html',
    styleUrls: ['./../input/input.scss'],
})
export class InputTreeSelectComponent implements OnInit, OnChanges {
    field: FieldConfig;
    group: FormGroup;
    data: any = {
        title: '',
        parentId: null,
        levelOpen: null,
        listId: null,
        treeData: [],
        isFolderSelect: false,
        isChildrenTree: false,
        selectElement: {},
        listViewTreeAsFolder: false,
    };
    eventAdd?: EventEmitter<any> = new EventEmitter<any>();
    /**
     * Данные дерева по ИД
     */
    dataTreeId = {};

    /**
     * Ссылка на элемент формы
     */
    formControl: FormControl = null;
    /**
     * Массив предков
     */
    parentsIds = [];

    /**
     * Тултип для самого поля
     */
    public tooltip = new BehaviorSubject<string | null>(null);

    public treeDialogRef: MatDialogRef<TreeSelectDialogComponent>;

    /**
     * Конструктор
     * @param dialog диалог
     * @param appService сервис ко внешним ресурсам
     */
    constructor(
        public appService: AppService,
        public dialog: MatDialog,
    ) {}

    ngOnInit() {
        this.formControl = new FormControl('', this.field.required ? [Validators.required] : []);
        this.setOptions({});
        if (this.field?.disabled) {
            this.formControl.disable({ onlySelf: true, emitEvent: false });
        }
        this.group.get(this.field.name).statusChanges.subscribe((status) => {
            switch (status) {
                case 'INVALID':
                    this.formControl.setErrors(this.group.get(this.field.name).errors);
                    this.formControl.markAsTouched();
            }
        });
        if (typeof this.data.treeData.subject === 'object') {
            this.data.treeData.subject.subscribe(() => {
                this.setOptions({});
            });
        }

        this.tooltip.next(this.formControl.value);
        this.group.get(this.field.name).valueChanges.subscribe((value) => {
            this.getNameField(value);
            this.tooltip.next(this.formControl.value);
        });
    }

    /**
     * Изменение параметров компонента
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.field) {
            if (changes.field.currentValue.required) {
                this.formControl.addValidators(Validators.required);
            } else {
                this.formControl.removeValidators(Validators.required);
            }
        }
    }

    setOptions(options) {
        Object.assign(this.data, options);
        if (Array.isArray(options) && options) {
            this.data.treeData = options;
        }
        this.dataTreeId = {};
        this.data.treeData.forEach((value) => {
            this.dataTreeId[value.id] = value.name;
        });
        if (this.treeDialogRef?.componentInstance?.treeComponentSelectDialog) {
            this.treeDialogRef.componentInstance.treeComponentSelectDialog.config.listNodes = options;
        }
        this.getNameField(this.group.get(this.field.name).value);
        this.treeDialogRef?.componentInstance?.treeComponentSelectDialog?.componentRef?.instance.setOptions();
    }

    /**
     * Получение строки в поле
     */
    getNameField(value) {
        this.parentsIds = [];

        this.getParentNodeFromArray(value, this.data.treeData);

        this.parentsIds.reverse();
        let valueString: string = '';

        if (this.field.multiple) {
            if (Array.isArray(value) && value.length) {
                const names: string[] = [];
                const showParents = this.field.name === 'subject_value';

                value.forEach((val) => {
                    if (this.dataTreeId[val]) {
                        if (showParents || !this.parentsIds.includes(val)) {
                            names.push(this.dataTreeId[val]);
                        }
                    }
                });
                valueString = names.join(', ');
            }
        } else {
            if (this.data.listViewTreeAsFolder && this.parentsIds.length) {
                let strParent = '';
                this.parentsIds.forEach((val) => {
                    strParent += this.dataTreeId[val] + '/';
                });
                valueString = this.dataTreeId[value] ? strParent + this.dataTreeId[value] : '';
            } else {
                valueString = this.dataTreeId[value] ? this.dataTreeId[value] : '';
            }
        }

        this.formControl.setValue(stripHtml(valueString));
    }

    /**
     * Получение родительских элементов
     * @param node - id записи
     * @param data - массив значений полей
     */
    getParentNodeFromArray(node = null, data = null): any {
        if (node && data) {
            data.forEach((key) => {
                if (typeof node === 'object') {
                    if (node.find((item) => item == key.id)) {
                        this.parentsIds.push(key.parent_id);
                        return this.getParentNodeFromArray(key.parent_id, data);
                    }
                } else {
                    if (node == key.id && key.parent_id != key.root_id) {
                        // TODO: Убрал главный родительский узел из инпута
                        // Если он должен будет отображаться в других местах, то надо будет вернуть
                        // И обдумать этот момент
                        if (key.parent_id != 1) {
                            this.parentsIds.push(key.parent_id);
                        }
                        return this.getParentNodeFromArray(key.parent_id, data);
                    }
                }
            });
        }
    }

    /**
     * Редактирование поля
     */
    editDataInput() {
        this.treeDialogRef = this.dialog.open(TreeSelectDialogComponent, {
            disableClose: true,
            autoFocus: '.input-search-value',
            width: '600px',
            data: {
                title: this.data.title,
                data: {
                    isMultiple: this.field.multiple,
                    parentId: this.data.parentId,
                    levelOpen: this.data.levelOpen,
                    treeData: this.data.treeData,
                    isChildrenTree: this.data?.isChildrenTree,
                    isFolderSelect: this.data.isFolderSelect,
                    useDefaultIcons: this.data?.useDefaultIcons,
                    value: this.group?.get(this.field.name)?.value,
                    listId: this.data.listId,
                    buttonAdd: this.field?.buttonAdd,
                    eventAdd: this.eventAdd,
                },
            },
        });
        this.treeDialogRef.keydownEvents().subscribe((result) => {
            if (result.code === 'Escape') {
                this.treeDialogRef.close({ save: false });
            }
        });
        this.treeDialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (!result.save) {
                    return;
                }
                this.data.selectElement = result.data.selected;
                if (this.field.multiple) {
                    const selected = [];
                    result.data.selected.forEach((node) => {
                        if (!this.data.isChildrenTree && (!node.is_folder || node.is_folder == '0')) {
                            selected.push(node.id);
                        }
                        if (this.data.isChildrenTree && !node.is_children) {
                            selected.push(node.id);
                        }
                    });
                    this.group.get(this.field.name).setValue(selected);
                } else {
                    this.group.get(this.field.name).setValue(result.data.id);
                }
                this.group.get(this.field.name).markAsDirty();
            });
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            this.formControl.disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
            this.formControl.enable({ onlySelf: true, emitEvent: false });
        }
    }
}
